import { useEffect, useMemo, useState } from 'react'
import type { Progress, SessionRecord } from '@/domain/session'
import { currentWeekNumber, findWeek } from '@/lib/weeks'
import { BackupDialog } from '@/features/panel/BackupDialog'
import { PanelScreen } from '@/features/panel/PanelScreen'
import { WorkoutOverlay } from '@/features/workout/WorkoutOverlay'
import { useChallengeStore } from '@/store/challengeStore'
import { useProgressStore } from '@/store/progressStore'
import { useSessionStore } from '@/store/sessionStore'

/** La app del alumno: panel del reto y cronómetro. */
export default function App() {
  const challenge = useChallengeStore((state) => state.challenge)
  const syncChallenge = useChallengeStore((state) => state.sync)

  const progress = useProgressStore((state) => state.progress)
  const load = useProgressStore((state) => state.load)
  const addSession = useProgressStore((state) => state.addSession)
  const replaceAll = useProgressStore((state) => state.replaceAll)

  const openSession = useSessionStore((state) => state.open)
  const closeSession = useSessionStore((state) => state.close)
  const activeWeek = useSessionStore((state) => state.week)

  const [weekNumber, setWeekNumber] = useState<number | null>(null)
  const [showBackup, setShowBackup] = useState(false)

  useEffect(() => {
    void load(challenge.id)
  }, [load, challenge.id])

  // Al abrir se busca la última versión publicada. Si no hay red, no pasa nada:
  // se sigue con la copia que ya estaba en el móvil.
  useEffect(() => {
    void syncChallenge()
  }, [syncChallenge])

  const week = useMemo(() => {
    const target = weekNumber ?? currentWeekNumber(challenge)
    return findWeek(challenge, target) ?? challenge.weeks[0]
  }, [challenge, weekNumber])

  // Con el entreno abierto, el panel de detrás no debe poder desplazarse.
  useEffect(() => {
    document.body.style.overflow = activeWeek ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [activeWeek])

  if (!week) return null

  const handleSave = async (session: Omit<SessionRecord, 'id'>) => {
    await addSession(session)
  }

  const handleRestore = async (restored: Progress) => {
    await replaceAll(restored)
  }

  return (
    <>
      <PanelScreen
        challenge={challenge}
        progress={progress}
        week={week}
        onSelectWeek={setWeekNumber}
        onStart={() => openSession(week)}
        onBackup={() => setShowBackup(true)}
      />

      {activeWeek && (
        <WorkoutOverlay
          challenge={challenge}
          week={activeWeek}
          progress={progress}
          onSave={handleSave}
          onClose={closeSession}
        />
      )}

      {showBackup && (
        <BackupDialog progress={progress} onRestore={handleRestore} onClose={() => setShowBackup(false)} />
      )}
    </>
  )
}
